/**
 * TextContentLightbox - MASTER RESPONSIVE CONTROLLER
 * 
 * This is the master component that controls ALL responsive behavior for text/research lightboxes.
 * 
 * ARCHITECTURE RULES:
 * - This component controls ALL responsive breakpoints (lg: 1024px)
 * - Child components (PerplexityContentRenderer, AppIdeaContentRenderer, etc.) should NOT have responsive logic
 * - All layout, sizing, and breakpoint behavior is managed here
 * 
 * RESPONSIVE STRATEGY:
 * - Mobile (0-1023px): Stacked layout (flex-col) with sidebar below content
 * - Desktop (1024px+): Side-by-side layout (lg:flex-row) with fixed sidebar width
 * - Single breakpoint at 1024px eliminates broken middle ground
 * 
 * USAGE CHAIN:
 * ResearchLightboxUniversal → PerplexityContentLightbox → TextContentLightbox (this)
 * AppIdeaContentLightbox → TextContentLightbox (this)
 * 
 * DO NOT add responsive classes (md:, lg:, sm:) to child components!
 */
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogClose,
  DialogTitle,
  DialogDescription
} from "@/components/ui/dialog";
import { X } from 'lucide-react';
import { useIsMobile } from '@/hooks/layout/use-mobile';
import { useTextContentNavigation } from './hooks/useTextContentNavigation';
import { 
  UniversalTextContentLightboxProps, 
  TextContentLightboxConfig,
  TextContentContext,
  TextContentSidebarContext 
} from './types/TextContentLightbox';

const DEFAULT_CONFIG: TextContentLightboxConfig = {
  preloading: {
    enabled: true,
    strategy: 'smart',
    preloadDistance: 2
  },
  navigation: {
    wheel: false,     // Disabled for text content - wheel is for scrolling
    keyboard: true,   // Arrow keys for navigation
    buttons: true     // Previous/Next buttons
  },
  mobile: {
    fullscreen: true,
    sidebarPosition: 'bottom'
  },
  features: {
    search: false,
    copy: true,
    download: true,
    print: false
  }
};

export const TextContentLightbox = <T,>({
  item,
  isOpen,
  onClose,
  allItems = [],
  onNavigate,
  loadMore,
  hasNextPage = false,
  isFetchingNextPage = false,
  accessors,
  renderContent,
  renderSidebar,
  config = DEFAULT_CONFIG,
  isLoading = false,
  loadingComponent,
  onDelete,
  onDownload,
  checkOwnership
}: UniversalTextContentLightboxProps<T>) => {
  // Early return if not open to prevent unnecessary hook calls
  if (!isOpen) {
    return null;
  }

  // Early return if no item to prevent null reference errors
  if (!item) {
    return null;
  }

  const { isMobile } = useIsMobile();

  // Merge config with defaults
  const mergedConfig = {
    ...DEFAULT_CONFIG,
    ...config,
    preloading: { ...DEFAULT_CONFIG.preloading, ...config?.preloading },
    navigation: { ...DEFAULT_CONFIG.navigation, ...config?.navigation },
    mobile: { ...DEFAULT_CONFIG.mobile, ...config?.mobile },
    features: { ...DEFAULT_CONFIG.features, ...config?.features }
  };

  // Use smart navigation hook (adapted from ImageLightbox)
  const {
    hasPrevious,
    hasNext,
    canNavigateNext,
    handlePrevious,
    handleNext,
    currentIndex,
    totalItems
  } = useTextContentNavigation({
    currentItem: item,
    allItems,
    hasNextPage,
    isFetchingNextPage,
    loadMore,
    onNavigate,
    getItemId: accessors.getContentId,
    config: mergedConfig
  });

  // Create content context
  const contentContext: TextContentContext<T> = {
    item,
    currentIndex,
    totalItems,
    isLoading,
    contentType: accessors.getContentType(item)
  };

  // Create sidebar context
  const sidebarContext: TextContentSidebarContext<T> = {
    item,
    hasPrevious,
    hasNext,
    isLoadingMore: isFetchingNextPage,
    hasNextPage,
    onPrevious: mergedConfig.navigation?.buttons ? handlePrevious : undefined,
    onNext: mergedConfig.navigation?.buttons ? handleNext : undefined,
    onLoadMore: loadMore,
    currentIndex,
    totalItems,
    isLoading
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent 
        showCloseButton={false}
        className={`
          ${mergedConfig.mobile?.fullscreen 
            ? 'h-screen w-screen md:max-w-[1200px] md:h-[90vh] md:w-[90vw]' 
            : 'max-w-[95vw] max-h-[95vh] w-auto h-auto'
          } 
          p-0 cursor-default
          ${isMobile ? '!top-0 !left-0 !transform-none !translate-x-0 !translate-y-0' : ''}
        `}
      >
        <DialogTitle className="sr-only">
          {accessors.getContentTitle(item)}
        </DialogTitle>
        <DialogDescription className="sr-only">
          Text content viewer for {accessors.getContentType(item)} content
        </DialogDescription>
        
        {/* MASTER RESPONSIVE CONTAINER - Controls ALL responsive behavior for text lightboxes */}
          <div className={`
            relative flex 
            ${mergedConfig.mobile?.sidebarPosition === 'bottom' ? 'flex-col lg:flex-row' : 'flex-col lg:flex-row'} 
          w-full h-full overflow-y-auto overflow-x-hidden
          `}>
            
            {/* Close button */}
          <div className="fixed top-4 right-6 z-50">
              <DialogClose className="rounded-full h-8 w-8 flex items-center justify-center bg-background/90 hover:bg-background border border-border shadow-sm transition-all cursor-pointer">
                <X className="h-4 w-4" />
              </DialogClose>
            </div>
          
          {/* Main content area - Master controls padding responsively */}
          <div className="flex-1 min-w-0">
            <div className="p-3 lg:p-6">
              {isLoading && loadingComponent ? (
                loadingComponent
              ) : (
                renderContent(item, contentContext)
              )}
            </div>
            </div>
            
          {/* Sidebar - Master controls width and height responsively */}
          <div className="w-full lg:w-[250px] flex-shrink-0 flex flex-col lg:h-full">
            <div className="flex flex-col lg:h-full">
                {renderSidebar(item, sidebarContext)}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default TextContentLightbox; 